<template>
  <div class="bg-white border border-gray-200 rounded-lg p-6">
    <h4 class="text-lg font-semibold text-black mb-4">
      Télécharger vos étiquettes Colissimo
    </h4>

    <!-- Bouton d'entrée (page détail) : ouvre la modal facture / dimensions / génération -->
    <div v-if="showGenerateEntryButton" class="mt-4">
      <p class="mb-3 text-sm text-gray-700">
        Renseignez la facture, les dimensions et le poids des colis pour générer
        vos étiquettes.
      </p>
      <button
        type="button"
        class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        @click="emit('request-generate-labels')"
      >
        <i class="fas fa-tags mr-2"></i>
        Générer vos étiquettes
      </button>
    </div>

    <!-- Formulaire de génération (dans la modal, après confirmation) -->
    <div v-else-if="showGenerateForm">
      <form @submit.prevent class="space-y-4">
        <!-- Poids des colis -->
        <div v-if="!props.useParcelDimensions" class="mt-4">
          <table class="min-w-full border border-gray-300 rounded-md">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-4 py-2 text-left text-sm font-medium text-black border-b"
                >
                  Colis
                </th>
                <th
                  class="px-4 py-2 text-left text-sm font-medium text-black border-b"
                >
                  Poids (kg)
                </th>
                <th class="px-4 py-2 border-b"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(poids, index) in colisPoids"
                :key="index"
                class="border-b"
              >
                <td class="px-4 py-2">
                  <label class="text-sm font-medium text-black"
                    >Colis n°{{ index + 1 }}</label
                  >
                </td>
                <td class="px-4 py-2">
                  <input
                    :value="colisPoids[index]"
                    type="number"
                    step="0.001"
                    min="0"
                    max="25"
                    class="numbersonly w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    @wheel="(e) => (e.target as HTMLInputElement).blur()"
                    @keydown="blockNonNumeric"
                    @paste="handlePaste"
                    @input="(e) => handlePoidsChange(index, e)"
                  />
                </td>
                <td class="px-4 py-2">
                  <button
                    v-if="colisPoids.length > 1"
                    type="button"
                    @click="removeColis(index)"
                    class="px-2 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors"
                    title="Supprimer ce colis"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <button
            type="button"
            @click="addColis"
            class="mt-3 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <i class="fas fa-plus mr-2"></i>Ajouter un colis
          </button>
          <p class="mt-2 text-xs text-red-600">
            Le poids d'un colis ne doit pas dépasser 25 kg.
          </p>
        </div>

        <!-- Bouton générer -->
        <div class="mt-4">
          <p
            v-if="props.dimensionErrors && props.dimensionErrors.length > 0"
            class="mb-2 text-sm text-red-600"
          >
            <i class="fas fa-exclamation-circle mr-1"></i>
            {{ props.dimensionErrors.join(" | ") }}
          </p>
          <button
            type="button"
            :disabled="isGenerating || !isFormValid || !canGenerateLabels"
            @click.prevent.stop="generateLabels"
            class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <span v-if="isGenerating">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              Génération en cours...
            </span>
            <span v-else>
              Générer vos étiquettes
            </span>
          </button>

          <p v-if="!isFormValid && formValidationMessage" class="mt-2 text-sm text-red-600">
            <i class="fas fa-exclamation-circle mr-1"></i>
            {{ formValidationMessage }}
          </p>
        </div>
      </form>
    </div>

    <!-- Liste des étiquettes existantes -->
    <div
      v-else-if="
        hasLabels &&
        (Number(orderStatus) === 4 ||
          Number(orderStatus) === 2 ||
          (isGroupage && (Number(orderStatus) === 7 || Number(orderStatus) === 8)))
      "
      class="mt-4"
    >
      <div
        v-if="hasUploadedInvoices === false"
        class="mb-4 p-4 bg-red-50 border border-red-200 rounded-md"
      >
        <p class="text-sm text-red-800">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <strong>Attention :</strong> Vous devez uploader une facture avant de
          pouvoir télécharger les étiquettes.
        </p>
      </div>
      <DownloadAllLabelsButton
        v-if="hasUploadedInvoices === true"
        :order-id="orderId"
        :label-count="labels.length"
      />
      <div class="overflow-x-auto">
        <table class="min-w-full border border-gray-300 rounded-md table-auto">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-4 py-2 text-left text-sm font-medium text-black border-b"
                style="width: 130px"
              >
                N° de colis
              </th>
              <th
                class="px-4 py-2 text-left text-sm font-medium text-black border-b"
                style="width: 130px"
              >
                Numéro de suivi
              </th>
              <th
                class="px-4 py-2 text-left text-sm font-medium text-black border-b"
                style="width: 200px"
              >
                Étiquettes
              </th>
              <th
                v-if="isAdmin"
                class="px-4 py-2 text-left text-sm font-medium text-black border-b"
                style="width: 120px"
              >
                Admin
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(etiquette, index) in labels"
              :key="etiquette.id_commande_etiquette"
              class="border-b hover:bg-gray-50"
            >
              <td class="px-4 py-2 text-sm">Colis {{ index + 1 }}</td>
              <td class="px-4 py-2">
                <a
                  v-if="
                    getTrackingUrlForLabel(
                      etiquette.tracking_number,
                      etiquette.id_livraison,
                    )
                  "
                  :href="getTrackingUrlForLabel(etiquette.tracking_number, etiquette.id_livraison)!"
                  target="_blank"
                  class="text-indigo-600 hover:text-indigo-800 underline text-sm font-medium"
                >
                  <strong>{{ etiquette.tracking_number }}</strong>
                </a>
                <strong v-else class="text-sm">{{
                  etiquette.tracking_number
                }}</strong>
              </td>
              <td class="px-4 py-2">
                <a
                  v-if="hasUploadedInvoices === true"
                  :href="etiquette.url"
                  target="_blank"
                  class="text-pink-600 hover:text-pink-800 underline text-sm"
                >
                  Téléchargez l'étiquette colis
                </a>
                <span
                  v-else
                  class="text-black text-sm cursor-not-allowed"
                  title="Veuillez uploader une facture pour télécharger l'étiquette"
                >
                  Téléchargez l'étiquette colis
                </span>
              </td>
              <td v-if="isAdmin" class="px-4 py-2">
                <AdminLabelDeleteButton
                  v-if="orderId"
                  :order-id="orderId"
                  :label-id="etiquette.id_commande_etiquette"
                  :tracking-number="etiquette.tracking_number"
                  @deleted="handleLabelDeleted"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Loading des étiquettes -->
    <div v-if="isLoadingLabels" class="mt-4 text-center py-4">
      <i class="fas fa-spinner fa-spin text-black"></i>
      <span class="ml-2 text-sm text-black"
        >Chargement des étiquettes...</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  inject,
  type ComputedRef,
} from "vue";
import {
  getOrderLabels,
  getUploadedInvoices,
  generateLabelEasy,
} from "@/api/orders";
import type {
  OrderLabel,
  LabelPropsStandard,
  OrderDetailResponse,
} from "@/types";
import { getTrackingUrl } from "@/utils/trackingUrl";
import { useNotification } from "@/composables/useNotification";
import {
  getTotalPoidsNonRupture,
  distributePoidsColis,
  getColisPoidsValidationMessage,
} from "@/utils/parcelWeights";
import AdminLabelDeleteButton from "@/components/order/labels/AdminLabelDeleteButton.vue";
import DownloadAllLabelsButton from "@/components/order/labels/DownloadAllLabelsButton.vue";
import { useAuthStore } from "@/stores/auth";

const authStore = useAuthStore();
const isAdmin = computed(() => authStore.isImpersonating);


const props = withDefaults(defineProps<LabelPropsStandard>(), {
  carrierName: "Colissimo",
  nbColisMax: 1,
  poidsTotal: 0,
  canGenerateLabels: true,
  dimensionErrors: () => [],
  generationEntry: "form",
});

// Injecter les données de la commande depuis la modal
const orderDetailDataFromInject = inject<
  ComputedRef<{ value: OrderDetailResponse | null }>
>(
  "orderDetailData",
  computed(() => ({ value: null })),
);
const orderData = computed(() => orderDetailDataFromInject.value?.value);

// Déterminer si c'est un groupage
const isGroupage = computed(() => {
  if (!orderData.value) return false;
  const groupageValue = orderData.value.commandeGroupe?.groupage;
  return (
    groupageValue === 1 ||
    String(groupageValue) === "1" ||
    orderData.value.groupage === true
  );
});

// Déterminer l'ID correct pour rechercher les étiquettes
const searchOrderId = computed(() => {
  if (!props.orderId) return null;
  return props.orderId;
});

// Watcher pour recharger les étiquettes quand orderDetailData change
watch(
  () => orderDetailDataFromInject.value.value,
  (newOrderDetailData) => {
    if (newOrderDetailData) {
      loadLabels();
    }
  },
  { immediate: false }, // Ne pas charger immédiatement, attendre que les données soient disponibles
);

const hasUploadedInvoices = ref(false);
// Vérifier les factures existantes
const checkInvoices = async () => {
  if (!props.orderId) return;
  try {
    const invoices = await getUploadedInvoices(props.orderId);
    hasUploadedInvoices.value = invoices.length > 0;
  } catch (error) {
    hasUploadedInvoices.value = false;
  }
};

// Écouter les événements d'upload de facture
const handleInvoiceUploaded = () => {
  checkInvoices();
};

// Nombre de colis fixe (basé sur la commande, non modifiable)
const nbColis = computed(() => props.nbColisMax || 1);
const colisPoids = ref<number[]>([]);
const isGenerating = ref(false);
const labels = ref<OrderLabel[]>([]);
const isLoadingLabels = ref(false);
watch(
  () => [nbColis.value, orderData.value?.lignes],
  () => {
    const newVal = nbColis.value;
    const totalPoids = getTotalPoidsNonRupture(orderData.value?.lignes);
    const poidsDistribues = distributePoidsColis(totalPoids, newVal);

    colisPoids.value = [...poidsDistribues];
  },
  { immediate: true },
);

const addColis = () => {
  colisPoids.value.push(0);
};

const removeColis = (index: number) => {
  if (colisPoids.value.length <= 1) return;
  colisPoids.value.splice(index, 1);
};

const hasLabels = computed(() => labels.value.length > 0);

const numericOrderStatus = computed(() => Number(props.orderStatus));

const canRequestOrGenerateLabels = computed(() => {
  const status = numericOrderStatus.value;
  return ![1, 3, 9].includes(status) && status > 0 && !hasLabels.value;
});

const showGenerateForm = computed(
  () => props.generationEntry === "form" && canRequestOrGenerateLabels.value,
);

const showGenerateEntryButton = computed(
  () => props.generationEntry === "button" && canRequestOrGenerateLabels.value,
);

const handleLabelDeleted = () => {
  loadLabels();
};

// Charger les étiquettes depuis l'API
const loadLabels = async () => {
  if (!searchOrderId.value) return;

  isLoadingLabels.value = true;
  try {
    const etiquettes = await getOrderLabels(searchOrderId.value);
    labels.value = etiquettes;
  } catch (error) {
    labels.value = [];
  } finally {
    isLoadingLabels.value = false;
  }
};

// Utiliser la fonction utilitaire pour générer l'URL de tracking
const getTrackingUrlForLabel = (
  trackingNumber: string,
  idLivraison: number,
): string | null => {
  const langue = "fr"; // TODO: Récupérer depuis le store ou les props
  return getTrackingUrl(trackingNumber, idLivraison, langue);
};

// Watch pour forcer la mise à jour quand la prop change
onMounted(() => {
  // Vérifier les factures au montage
  checkInvoices();
  // Ne charger les étiquettes que si orderDetailData est déjà disponible
  if (orderDetailDataFromInject.value.value) {
    loadLabels();
  }
  // Écouter les événements d'upload de facture
  window.addEventListener("invoiceUploaded", handleInvoiceUploaded);
  window.addEventListener("invoiceDeleted", handleInvoiceUploaded);
});

onUnmounted(() => {
  // Nettoyer les event listeners
  window.removeEventListener("invoiceUploaded", handleInvoiceUploaded);
  window.removeEventListener("invoiceDeleted", handleInvoiceUploaded);
});
const formValidationMessage = computed(() => {
  const poidsMessage = getColisPoidsValidationMessage(effectiveColisPoids.value);
  if (poidsMessage) return poidsMessage;
  return "Veuillez remplir tous les champs obligatoires";
});

const effectiveColisPoids = computed(() =>
  props.useParcelDimensions ? props.parcelWeights || [] : colisPoids.value,
);

// Validation du formulaire
const isFormValid = computed(() => {
  // Vérifier que tous les poids des colis sont remplis (> 0)
  const allPoidsFilled = effectiveColisPoids.value.every(
    (poids) => poids > 0 && poids <= 25,
  );
  if (!allPoidsFilled) return false;

  return true;
});

const emit = defineEmits<{
  "labels-generated": [];
  "request-generate-labels": [];
}>();

const { success, error: showError } = useNotification();

const generateLabels = async () => {
  // Empêcher les clics multiples - double vérification
  if (isGenerating.value) {
    console.warn("Génération déjà en cours, ignore le clic");
    return;
  }
  isGenerating.value = true;
  console.log("Début génération étiquettes pour commande", props.orderId);
  try {
    const response = await generateLabelEasy({
      id_commande: props.orderId,
      nbColis: effectiveColisPoids.value.length,
      colisPoids: effectiveColisPoids.value,
      parcelDimensions: props.parcelDimensions,
      invoiceNumber: "", // Valeur vide par défaut
      invoiceDate: "", // Valeur vide par défaut
    });

    if (response.success && response.etiquettes) {
      console.log(
        "Étiquettes générées avec succès, nombre:",
        response.etiquettes.length,
      );
      success("Étiquettes générées avec succès");
      labels.value = response.etiquettes;
      console.log("Émission événement labels-generated");
      emit("labels-generated");
    } else {
      showError(
        response.error || "Erreur lors de la génération des étiquettes",
      );
    }
  } catch (err: unknown) {
    const errorMessage =
      err instanceof Error
        ? err.message
        : "Erreur lors de la génération des étiquettes";
    showError(errorMessage);
  } finally {
    isGenerating.value = false;
  }
};

const blockNonNumeric = (event: KeyboardEvent) => {
  const allowed = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];
  if (allowed.includes(event.key)) return;
  if (
    (event.ctrlKey || event.metaKey) &&
    ["a", "c", "v", "x"].includes(event.key)
  )
    return;
  if (event.key === "." || event.key === ",") return;
  if (event.key < "0" || event.key > "9") event.preventDefault();
};

const handlePaste = (event: ClipboardEvent) => {
  const paste = event.clipboardData?.getData("text") || "";
  if (!/^\d*\.?\d*$/.test(paste)) event.preventDefault();
};

const handlePoidsChange = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement | null;
  if (!target) return;
  const value = target.value ? Number(target.value) : 0;
  colisPoids.value[index] = value;
};

</script>

<style scoped>
.numbersonly {
  -moz-appearance: textfield;
  appearance: textfield;
}
.numbersonly::-webkit-outer-spin-button,
.numbersonly::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}
</style>
